import { isSupabaseConfigured, missingSupabaseConfig } from "./config";
import { supabase } from "./client";
import {
  DEFAULT_BRANCH_COMMISSION_RATE,
  DEFAULT_IA_COMMISSION_RATE,
  DEFAULT_KIOSK_COMMISSION_RATE,
  DEFAULT_MENU_QR_COMMISSION_RATE,
  DEFAULT_BRANCH_PAYMENT_SPLIT_MODE,
  PAYMENT_SPLIT_MODES,
  normalizeCommissionRate,
  normalizePaymentSplitMode,
} from "../local/paymentSettings";
import {
  getOperationalMenuQrWalletProviders,
  normalizeMenuQrWalletProviders,
} from "../payments/menuQrWalletProviders";

const PLATFORM_BACKOFFICE_ADMIN_EMAILS = new Set([
  "diegobortportillo@gmail.com",
]);
const COMMISSION_LEDGER_FACTORS = Object.freeze({
  debt_created: 1,
  debt_adjusted: 1,
  debt_applied: -1,
  debt_cleared: -1,
});
const BACKOFFICE_READ_CACHE_TTL_MS = 15 * 1000;
const backofficeReadCache = new Map();

const ensureSupabaseReady = () => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      `Faltan variables de entorno para Supabase: ${missingSupabaseConfig.join(", ")}`
    );
  }
};

const normalizeError = (error, fallbackMessage) => {
  if (!error) {
    return new Error(fallbackMessage);
  }

  return new Error(error.message || fallbackMessage);
};

const cloneStructuredValue = (value) => JSON.parse(JSON.stringify(value));

const pruneExpiredBackofficeReadCache = () => {
  const now = Date.now();

  Array.from(backofficeReadCache.entries()).forEach(([cacheKey, entry]) => {
    if (!entry || entry.expiresAt <= now) {
      backofficeReadCache.delete(cacheKey);
    }
  });
};

const getCachedBackofficeRead = (cacheKey) => {
  pruneExpiredBackofficeReadCache();

  const entry = backofficeReadCache.get(cacheKey);

  if (!entry || entry.expiresAt <= Date.now()) {
    if (entry) {
      backofficeReadCache.delete(cacheKey);
    }

    return null;
  }

  return cloneStructuredValue(entry.value);
};

const saveCachedBackofficeRead = (cacheKey, value) => {
  if (!cacheKey) {
    return cloneStructuredValue(value);
  }

  pruneExpiredBackofficeReadCache();
  backofficeReadCache.set(cacheKey, {
    value: cloneStructuredValue(value),
    expiresAt: Date.now() + BACKOFFICE_READ_CACHE_TTL_MS,
  });

  if (backofficeReadCache.size > 64) {
    Array.from(backofficeReadCache.entries())
      .sort((left, right) => left[1].expiresAt - right[1].expiresAt)
      .slice(0, backofficeReadCache.size - 40)
      .forEach(([staleKey]) => {
        backofficeReadCache.delete(staleKey);
      });
  }

  return cloneStructuredValue(value);
};

export const invalidateBackofficeReadCache = (companyId = "") => {
  const safeCompanyId = toSafeText(companyId, 120);

  if (!safeCompanyId) {
    backofficeReadCache.clear();
    return;
  }

  Array.from(backofficeReadCache.keys()).forEach((cacheKey) => {
    if (
      cacheKey === "platform:companies" ||
      cacheKey.startsWith(`company-settings:${safeCompanyId}`) ||
      cacheKey.startsWith(`company-report:${safeCompanyId}`) ||
      cacheKey.startsWith(`company-ai-report:${safeCompanyId}`)
    ) {
      backofficeReadCache.delete(cacheKey);
    }
  });
};

const toSafeText = (value, maxLength = 160) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const toMoney = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
};

const toPreciseNumber = (value, decimals = 8) => {
  const parsed = Number(value || 0);

  if (!Number.isFinite(parsed)) {
    return 0;
  }

  const factor = 10 ** Math.max(0, Math.round(decimals));
  return Math.round(parsed * factor) / factor;
};

const pickPayloadValue = (payload, camelKey, snakeKey = "") => {
  if (
    payload &&
    typeof payload === "object" &&
    Object.prototype.hasOwnProperty.call(payload, camelKey)
  ) {
    return payload[camelKey];
  }

  if (
    snakeKey &&
    payload &&
    typeof payload === "object" &&
    Object.prototype.hasOwnProperty.call(payload, snakeKey)
  ) {
    return payload[snakeKey];
  }

  return undefined;
};

const isOperationalStateUnavailableError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42P01" ||
    error?.code === "42501" ||
    message.includes("branch_operational_state") ||
    message.includes("row-level security")
  );
};

const normalizePreparationNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null;
};

const normalizeJsonRecord = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const normalizeDebugStringList = (value, maxItems = 12, maxLength = 160) =>
  (Array.isArray(value) ? value : [])
    .map((entry) => toSafeText(entry, maxLength))
    .filter(Boolean)
    .filter((entry, index, collection) => collection.indexOf(entry) === index)
    .slice(0, maxItems);

const normalizeAiConversationSnapshot = (value) =>
  (Array.isArray(value) ? value : [])
    .map((entry, index) => {
      const payload = normalizeJsonRecord(entry);
      return {
        id: toSafeText(payload.id || `msg-${index + 1}`, 80),
        role:
          toSafeText(payload.role, 24).toLowerCase() === "assistant"
            ? "assistant"
            : "user",
        text: toSafeText(payload.text || payload.content, 400),
      };
    })
    .filter((entry) => entry.text)
    .slice(0, 8);

const normalizeAiCartSnapshot = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => {
      const payload = normalizeJsonRecord(item);
      return {
        productName: toSafeText(payload.productName || payload.name, 120),
        quantity: Math.max(1, Math.round(Number(payload.quantity || 1) || 1)),
        totalPrice: toMoney(payload.totalPrice),
        selectionDetails: normalizeDebugStringList(payload.selectionDetails, 6, 120),
      };
    })
    .filter((entry) => entry.productName)
    .slice(0, 8);

const buildOperationalItemsSummary = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => {
      const quantity = Math.max(1, Number(item?.quantity || 0) || 1);
      const productName = toSafeText(
        item?.productName ||
          item?.name ||
          item?.nombre ||
          item?.title ||
          item?.label ||
          "Producto",
        120
      );
      const selectionDetails = Array.isArray(item?.selectionDetails)
        ? item.selectionDetails
            .map((detail) => toSafeText(detail, 60))
            .filter(Boolean)
            .slice(0, 4)
        : [];

      if (!productName) {
        return "";
      }

      return selectionDetails.length > 0
        ? `${quantity}x ${productName} (${selectionDetails.join(" · ")})`
        : `${quantity}x ${productName}`;
    })
    .filter(Boolean)
    .slice(0, 24)
    .join(", ");

const buildOperationalContextRecord = (record = {}) => ({
  orderId: toSafeText(record?.originalOrderId || record?.id || record?.orderId, 160),
  paymentOperationId: toSafeText(record?.paymentOperationId, 160),
  customerName: toSafeText(record?.customerName || record?.customerAlias, 80),
  preparationNumber: normalizePreparationNumber(record?.preparationNumber),
  itemsSummary: toSafeText(
    record?.itemsSummary || buildOperationalItemsSummary(record?.items),
    900
  ),
  mesa: toSafeText(record?.mesa || record?.label || record?.tableLabel, 120),
  source: toSafeText(record?.source, 40),
  canal: toSafeText(record?.canal, 60),
  status: toSafeText(record?.status || record?.paymentStatus, 40),
  serviceType: toSafeText(record?.serviceType, 80),
});

const mergeOperationalContext = (current = {}, incoming = {}) => ({
  orderId: current.orderId || incoming.orderId || "",
  paymentOperationId: current.paymentOperationId || incoming.paymentOperationId || "",
  customerName: current.customerName || incoming.customerName || "",
  preparationNumber: current.preparationNumber || incoming.preparationNumber || null,
  itemsSummary: current.itemsSummary || incoming.itemsSummary || "",
  mesa: current.mesa || incoming.mesa || "",
  source: current.source || incoming.source || "",
  canal: current.canal || incoming.canal || "",
  status: current.status || incoming.status || "",
  serviceType: current.serviceType || incoming.serviceType || "",
});

const mergeLedgerMetadataWithOperationalContext = (metadata = {}, context = {}) => {
  const safeMetadata =
    metadata && typeof metadata === "object" && !Array.isArray(metadata) ? metadata : {};
  const mergedPreparationNumber =
    safeMetadata.preparationNumber ??
    safeMetadata.preparation_number ??
    context.preparationNumber ??
    null;

  return {
    ...safeMetadata,
    source: toSafeText(safeMetadata.source || context.source, 40),
    canal: toSafeText(safeMetadata.canal || context.canal, 60),
    status: toSafeText(safeMetadata.status || context.status, 40),
    customerName: toSafeText(safeMetadata.customerName || context.customerName, 80),
    preparationNumber: normalizePreparationNumber(mergedPreparationNumber),
    itemsSummary: toSafeText(
      safeMetadata.itemsSummary || safeMetadata.orderSummary || context.itemsSummary,
      900
    ),
    mesa: toSafeText(safeMetadata.mesa || context.mesa, 120),
    serviceType: toSafeText(safeMetadata.serviceType || context.serviceType, 80),
  };
};

const normalizeBranchServiceMode = (value) =>
  String(value || "").trim().toLowerCase() === "kiosk" ? "kiosk" : "salon";

const isMissingBackofficeCommissionColumnsError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42703" ||
    message.includes("menu_qr_commission_rate") ||
    message.includes("kiosk_commission_rate") ||
    message.includes("manual_order_commission_rate") ||
    message.includes("ia_commission_rate") ||
    message.includes("payment_split_mode")
  );
};

const isMissingBackofficeMenuQrWalletColumnsError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return error?.code === "42703" || message.includes("menu_qr_wallet_providers");
};

const isAiUsageEventsUnavailableError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42P01" ||
    error?.code === "42501" ||
    message.includes("ai_usage_events") ||
    message.includes("row-level security")
  );
};

const buildDefaultCompanyChannelCommissionSettings = () => ({
  manualOrderCommissionRate: DEFAULT_BRANCH_COMMISSION_RATE,
  menuQrCommissionRate: DEFAULT_MENU_QR_COMMISSION_RATE,
  kioskCommissionRate: DEFAULT_KIOSK_COMMISSION_RATE,
  iaCommissionRate: DEFAULT_IA_COMMISSION_RATE,
  paymentSplitMode: PAYMENT_SPLIT_MODES.MERCADO_PAGO_MARKETPLACE_FEE,
  menuQrWalletProviders: normalizeMenuQrWalletProviders([]),
  branches: [],
});

const normalizeCompanyStatus = (value) =>
  String(value || "").trim().toLowerCase() === "active" ? "active" : "inactive";

const buildDefaultPlatformBackofficeCompanySummary = (company = {}) => ({
  id: company?.id || "",
  name: company?.name || "Empresa",
  slug: company?.slug || "",
  legalName: company?.legal_name || "",
  phone: company?.phone || "",
  status: normalizeCompanyStatus(company?.status),
  createdAt: company?.created_at || "",
  billingEmail: "",
  ownerEmail: "",
  bankAlias: "",
  totalBranches: 0,
  activeBranches: 0,
  kioskBranches: 0,
  salonBranches: 0,
  walletBalanceUnits: 0,
  walletLowBalanceThreshold: 0,
  walletLastTransactionAt: "",
  allowNegativeBalance: false,
  negativeBalanceLimit: 0,
  manualOrderCommissionRate: DEFAULT_BRANCH_COMMISSION_RATE,
  menuQrCommissionRate: DEFAULT_MENU_QR_COMMISSION_RATE,
  kioskCommissionRate: DEFAULT_KIOSK_COMMISSION_RATE,
  iaCommissionRate: DEFAULT_IA_COMMISSION_RATE,
  paymentSplitMode: PAYMENT_SPLIT_MODES.MERCADO_PAGO_MARKETPLACE_FEE,
  menuQrWalletProviders: normalizeMenuQrWalletProviders([]),
  operationalWalletProvidersCount: 0,
});

const buildDefaultCompanyCommissionDebtReport = () => ({
  outstandingDebtAmount: 0,
  totalCreatedAmount: 0,
  totalAppliedAmount: 0,
  totalCollectedAmount: 0,
  totalCurrentCommissionCollectedAmount: 0,
  totalDebtRecoveredAmount: 0,
  entriesCount: 0,
  branches: [],
  recentEntries: [],
  invoiceEntries: [],
  collectionEntries: [],
  digitalTransactions: [],
});

const buildDefaultCompanyAiUsageReport = () => ({
  trackingReady: true,
  trackingMessage: "",
  entries: [],
  branches: [],
});

const calculateCommissionLedgerOutstanding = (entries = []) =>
  toMoney(
    (Array.isArray(entries) ? entries : []).reduce((sum, entry) => {
      const factor =
        COMMISSION_LEDGER_FACTORS[toSafeText(entry?.entry_type, 40)] || 0;
      return sum + toMoney(entry?.amount) * factor;
    }, 0)
  );

const formatCommissionLedgerChannelLabel = (entry = {}) => {
  const metadata =
    entry && typeof entry.metadata === "object" && !Array.isArray(entry.metadata)
      ? entry.metadata
      : {};
  const source = toSafeText(metadata.source, 40).toLowerCase();
  const canal = toSafeText(metadata.canal, 60).toLowerCase();
  const method = toSafeText(entry.source_method, 60).toLowerCase();

  if (source === "kiosk") {
    return method === "pay_by_phone" ? "Kiosk digital" : "Kiosk caja";
  }

  if (source === "app" || canal === "qr") {
    return method === "deep_link" ? "Menu QR digital" : "Menu QR caja";
  }

  if (source === "manual") {
    return "Manual / salon";
  }

  return "Operacion local";
};

const formatCommissionCollectionChannelLabel = (entry = {}) => {
  const sourceChannel = toSafeText(entry.source_channel, 80).toLowerCase();
  const sourceMethod = toSafeText(entry.source_method, 80).toLowerCase();

  if (sourceChannel === "delivery") {
    return "We! Pedidos / Delivery";
  }

  if (sourceChannel === "kiosk") {
    return "Kiosk digital";
  }

  if (sourceChannel === "waiter") {
    return "Mozo / Salon";
  }

  if (sourceChannel === "menu_qr") {
    return "Menu QR";
  }

  return sourceChannel || sourceMethod || "Cobro digital";
};

const buildBranchCommissionPreview = ({
  branch,
  branchSettings,
  manualOrderCommissionRate,
  menuQrCommissionRate,
  kioskCommissionRate,
  iaCommissionRate,
  paymentSplitMode,
}) => {
  const serviceMode = normalizeBranchServiceMode(branchSettings?.service_mode);

  return {
    branchId: branch?.id || "",
    branchName: branch?.name || "Sucursal",
    serviceMode,
    manualOrderCommissionRate,
    menuQrCommissionRate,
    kioskCommissionRate,
    iaCommissionRate,
    paymentSplitMode,
    currentStoredCommissionRate: normalizeCommissionRate(
      branchSettings?.commission_rate
    ),
  };
};

const normalizeCompanyChannelCommissionSettings = ({
  settings,
  branches = [],
  branchSettingsRows = [],
} = {}) => {
  const manualOrderCommissionRate = normalizeCommissionRate(
    settings?.manual_order_commission_rate ??
      settings?.commission_rate ??
      DEFAULT_BRANCH_COMMISSION_RATE
  );
  const menuQrCommissionRate = normalizeCommissionRate(
    settings?.menu_qr_commission_rate ?? DEFAULT_MENU_QR_COMMISSION_RATE
  );
  const kioskCommissionRate = normalizeCommissionRate(
    settings?.kiosk_commission_rate ?? DEFAULT_KIOSK_COMMISSION_RATE
  );
  const iaCommissionRate = normalizeCommissionRate(
    settings?.ia_commission_rate ?? DEFAULT_IA_COMMISSION_RATE
  );
  const paymentSplitMode = normalizePaymentSplitMode(
    settings?.payment_split_mode ?? DEFAULT_BRANCH_PAYMENT_SPLIT_MODE
  );
  const branchSettingsMap = new Map(
    (Array.isArray(branchSettingsRows) ? branchSettingsRows : []).map((row) => [
      row.branch_id,
      row,
    ])
  );

  return {
    manualOrderCommissionRate,
    menuQrCommissionRate,
    kioskCommissionRate,
    iaCommissionRate,
    paymentSplitMode,
    menuQrWalletProviders: normalizeMenuQrWalletProviders(
      settings?.menu_qr_wallet_providers
    ),
    branches: (Array.isArray(branches) ? branches : []).map((branch) =>
      buildBranchCommissionPreview({
        branch,
        branchSettings: branchSettingsMap.get(branch.id),
        manualOrderCommissionRate: normalizeCommissionRate(
          branchSettingsMap.get(branch.id)?.manual_order_commission_rate ??
            manualOrderCommissionRate
        ),
        menuQrCommissionRate: normalizeCommissionRate(
          branchSettingsMap.get(branch.id)?.menu_qr_commission_rate ??
            menuQrCommissionRate
        ),
        kioskCommissionRate: normalizeCommissionRate(
          branchSettingsMap.get(branch.id)?.kiosk_commission_rate ??
            kioskCommissionRate
        ),
        iaCommissionRate: normalizeCommissionRate(
          branchSettingsMap.get(branch.id)?.ia_commission_rate ?? iaCommissionRate
        ),
        paymentSplitMode: normalizePaymentSplitMode(
          branchSettingsMap.get(branch.id)?.payment_split_mode ?? paymentSplitMode
        ),
      })
    ),
  };
};

const loadCompanyBranches = async (companyId) => {
  const { data, error } = await supabase
    .from("branches")
    .select("id, name, status")
    .eq("company_id", companyId)
    .order("created_at", { ascending: true });

  if (error) {
    throw normalizeError(error, "No se pudieron cargar las sucursales para el Backoffice.");
  }

  return data || [];
};

const loadPlatformCompanies = async () => {
  const { data, error } = await supabase
    .from("companies")
    .select("id, name, slug, legal_name, phone, status, created_at")
    .order("status", { ascending: true })
    .order("name", { ascending: true });

  if (error) {
    throw normalizeError(
      error,
      "No se pudo cargar el listado global de empresas del Backoffice."
    );
  }

  return data || [];
};

const loadPlatformCompanySettingsRows = async () => {
  const { data, error } = await supabase
    .from("company_settings")
    .select(
      "company_id, billing_email, owner_email, bank_alias, manual_order_commission_rate, menu_qr_commission_rate, kiosk_commission_rate, ia_commission_rate, payment_split_mode, menu_qr_wallet_providers"
    );

  if (error) {
    if (isMissingBackofficeMenuQrWalletColumnsError(error)) {
      throw new Error(
        "Falta aplicar backoffice_menu_qr_wallet_providers_patch.sql para que el Backoffice global pueda leer deeplinks y billeteras."
      );
    }

    if (isMissingBackofficeCommissionColumnsError(error)) {
      throw new Error(
        "Falta aplicar backoffice_channel_commission_rates_patch.sql para que el Backoffice global pueda leer precios por canal."
      );
    }

    throw normalizeError(
      error,
      "No se pudo cargar la configuracion comercial de las empresas."
    );
  }

  return data || [];
};

const loadPlatformWalletRows = async () => {
  const { data, error } = await supabase
    .from("company_wallets")
    .select(
      "company_id, balance_units, low_balance_threshold, last_transaction_at, allow_negative_balance, negative_balance_limit"
    );

  if (error) {
    throw normalizeError(
      error,
      "No se pudo cargar el estado de billeteras MZ del Backoffice."
    );
  }

  return data || [];
};

const loadPlatformBranchRows = async () => {
  const { data, error } = await supabase
    .from("branches")
    .select("id, company_id, status");

  if (error) {
    throw normalizeError(
      error,
      "No se pudieron cargar las sucursales globales del Backoffice."
    );
  }

  return data || [];
};

const loadPlatformBranchSettingsRows = async () => {
  const { data, error } = await supabase
    .from("branch_settings")
    .select("branch_id, service_mode");

  if (error) {
    throw normalizeError(
      error,
      "No se pudo leer la modalidad de las sucursales para el Backoffice."
    );
  }

  return data || [];
};

const loadBranchSettingsRows = async (branchIds = []) => {
  const safeBranchIds = Array.from(new Set((branchIds || []).filter(Boolean)));

  if (safeBranchIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from("branch_settings")
    .select(
      "branch_id, service_mode, commission_rate, manual_order_commission_rate, menu_qr_commission_rate, kiosk_commission_rate, ia_commission_rate, payment_split_mode"
    )
    .in("branch_id", safeBranchIds);

  if (error) {
    if (isMissingBackofficeCommissionColumnsError(error)) {
      throw new Error(
        "Falta aplicar backoffice_channel_commission_rates_patch.sql en Supabase para guardar comisiones separadas por canal."
      );
    }

    throw normalizeError(
      error,
      "No se pudo leer la configuracion de comisiones de las sucursales."
    );
  }

  return data || [];
};

const loadBranchCommissionLedgerRows = async (branchIds = []) => {
  const safeBranchIds = Array.from(new Set((branchIds || []).filter(Boolean)));

  if (safeBranchIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from("branch_commission_ledger")
    .select(
      "id, branch_id, order_id, payment_operation_id, entry_type, amount, source_method, note, metadata, created_at"
    )
    .in("branch_id", safeBranchIds)
    .order("created_at", { ascending: false });

  if (error) {
    throw normalizeError(
      error,
      "No se pudo cargar el historial de deuda y compensaciones de comisiones."
    );
  }

  return data || [];
};

const loadBranchDigitalPaymentIntentRows = async (branchIds = []) => {
  const safeBranchIds = Array.from(new Set((branchIds || []).filter(Boolean)));

  if (safeBranchIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from("branch_digital_payment_intents")
    .select(
      "id, branch_id, order_id, payment_operation_id, provider, provider_operation_id, status, gross_amount, current_commission_amount, pending_commission_debt_amount, retained_total_amount, restaurant_net_amount, approved_at, created_at, metadata"
    )
    .in("branch_id", safeBranchIds)
    .order("created_at", { ascending: false });

  if (error) {
    throw normalizeError(
      error,
      "No se pudieron cargar las transacciones digitales del Backoffice."
    );
  }

  return data || [];
};

const loadPlatformCommissionCollectionRows = async (branchIds = []) => {
  const safeBranchIds = Array.from(new Set((branchIds || []).filter(Boolean)));

  if (safeBranchIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from("platform_commission_collections")
    .select(
      "id, branch_id, order_id, payment_operation_id, payment_intent_id, collection_type, amount, currency, status, source_channel, source_method, collected_at, metadata"
    )
    .in("branch_id", safeBranchIds)
    .order("collected_at", { ascending: false });

  if (error) {
    throw normalizeError(
      error,
      "No se pudieron cargar las comisiones efectivamente cobradas."
    );
  }

  return data || [];
};

const loadBranchOperationalStateRows = async (branchIds = []) => {
  const safeBranchIds = Array.from(new Set((branchIds || []).filter(Boolean)));

  if (safeBranchIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from("branch_operational_state")
    .select("branch_id, payload")
    .in("branch_id", safeBranchIds);

  if (error) {
    if (isOperationalStateUnavailableError(error)) {
      return [];
    }

    throw normalizeError(
      error,
      "No se pudo cargar el contexto operativo de las sucursales para el Backoffice."
    );
  }

  return data || [];
};

export const buildBackofficeDigitalTransactions = ({
  intentRows = [],
  collectionRows = [],
  branchesById = new Map(),
} = {}) => {
  const confirmedCollectionsByIntentId = (Array.isArray(collectionRows)
    ? collectionRows
    : []
  ).reduce((collection, entry) => {
    if (!entry.paymentIntentId || entry.status !== "confirmed") {
      return collection;
    }

    const current = collection.get(entry.paymentIntentId) || {
      currentCommissionAmount: 0,
      debtRecoveryAmount: 0,
      totalAmount: 0,
      entriesCount: 0,
      metadata: {},
    };
    current.totalAmount = toMoney(current.totalAmount + entry.amount);
    current.entriesCount += 1;
    current.metadata = {
      ...current.metadata,
      ...normalizeJsonRecord(entry.metadata),
    };

    if (entry.collectionType === "current_commission") {
      current.currentCommissionAmount = toMoney(
        current.currentCommissionAmount + entry.amount
      );
    }

    if (entry.collectionType === "debt_recovery") {
      current.debtRecoveryAmount = toMoney(
        current.debtRecoveryAmount + entry.amount
      );
    }

    collection.set(entry.paymentIntentId, current);
    return collection;
  }, new Map());

  return (Array.isArray(intentRows) ? intentRows : []).map((intent) => {
    const intentMetadata = normalizeJsonRecord(intent.metadata);
    const collectionSummary = confirmedCollectionsByIntentId.get(intent.id) || {
      currentCommissionAmount: 0,
      debtRecoveryAmount: 0,
      totalAmount: 0,
      entriesCount: 0,
      metadata: {},
    };
    const metadata = {
      ...intentMetadata,
      ...normalizeJsonRecord(collectionSummary.metadata),
    };
    const status = toSafeText(intent.status, 40).toLowerCase();
    const approved = ["approved", "paid", "closed"].includes(status);
    const refunded = status === "refunded";
    const expectedCollectedAmount = toMoney(intent.retained_total_amount);
    const collectedAmount = toMoney(collectionSummary.totalAmount);
    const collectionStatus = refunded
      ? "refunded"
      : approved &&
          (expectedCollectedAmount <= 0 ||
            collectedAmount + 0.009 >= expectedCollectedAmount)
        ? "reconciled"
        : approved
          ? "pending_reconciliation"
          : "pending_payment";

    return {
      id: intent.id || "",
      branchId: intent.branch_id || "",
      branchName:
        branchesById.get(intent.branch_id || "")?.branchName || "Sucursal",
      orderId: intent.order_id || "",
      paymentOperationId: intent.payment_operation_id || "",
      provider: toSafeText(intent.provider, 60),
      providerOperationId: toSafeText(
        intent.provider_operation_id || metadata.providerPaymentId,
        160
      ),
      status,
      collectionStatus,
      saleAmount: toMoney(metadata.commissionBaseAmount ?? intent.gross_amount),
      deliveryFeeAmount: toMoney(metadata.deliveryFeeAmount),
      chargedAmount: toMoney(
        metadata.chargedAmount ?? metadata.grossAmount ?? intent.gross_amount
      ),
      currentCommissionAmount: toMoney(intent.current_commission_amount),
      reservedDebtAmount: toMoney(intent.pending_commission_debt_amount),
      collectedCurrentCommissionAmount: toMoney(
        collectionSummary.currentCommissionAmount
      ),
      collectedDebtAmount: toMoney(collectionSummary.debtRecoveryAmount),
      collectedTotalAmount: collectedAmount,
      retainedTotalAmount: expectedCollectedAmount,
      restaurantNetAmount: toMoney(intent.restaurant_net_amount),
      collectionEntriesCount: collectionSummary.entriesCount,
      displayNumber: toSafeText(metadata.displayNumber, 80),
      itemsSummary: toSafeText(metadata.itemsSummary, 900),
      sourceChannel: toSafeText(metadata.sourceChannel, 80),
      sourceMode: toSafeText(
        metadata.sourceMode || metadata.commissionLedgerSourceMode,
        80
      ),
      channelLabel: formatCommissionCollectionChannelLabel({
        source_channel: metadata.sourceChannel,
        source_method:
          metadata.sourceMode || metadata.commissionLedgerSourceMode,
        metadata,
      }),
      transactionAt: intent.approved_at || intent.created_at || "",
      approvedAt: intent.approved_at || "",
      createdAt: intent.created_at || "",
      metadata,
    };
  });
};

const loadCompanyAiUsageRows = async (companyId) => {
  if (!companyId) {
    return {
      trackingReady: true,
      trackingMessage: "",
      rows: [],
    };
  }

  const { data, error } = await supabase
    .from("ai_usage_events")
    .select(
      "id, company_id, branch_id, order_id, qr_token, customer_session_id, provider, model, status, prompt_tokens, completion_tokens, total_tokens, cached_input_tokens, reasoning_tokens, estimated_input_cost_usd, estimated_output_cost_usd, estimated_total_cost_usd, usd_to_ars_rate, estimated_total_cost_ars, question_preview, response_preview, metadata, created_at"
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) {
    if (isAiUsageEventsUnavailableError(error)) {
      return {
        trackingReady: false,
        trackingMessage:
          "Falta aplicar ai_usage_events_patch.sql en Supabase para auditar el consumo real de IA.",
        rows: [],
      };
    }

    throw normalizeError(
      error,
      "No se pudo cargar el historial real de consumo IA."
    );
  }

  return {
    trackingReady: true,
    trackingMessage: "",
    rows: data || [],
  };
};

const buildOperationalContextIndexes = (stateRows = []) => {
  const paymentOperationContexts = new Map();
  const orderContexts = new Map();

  const saveContext = (map, key, context) => {
    if (!key) {
      return;
    }

    map.set(key, mergeOperationalContext(map.get(key), context));
  };

  (Array.isArray(stateRows) ? stateRows : []).forEach((row) => {
    const branchId = toSafeText(row?.branch_id, 120);
    const payload =
      row?.payload && typeof row.payload === "object" && !Array.isArray(row.payload)
        ? row.payload
        : {};
    const paymentOperations = Array.isArray(
      pickPayloadValue(payload, "paymentOperations", "payment_operations")
    )
      ? pickPayloadValue(payload, "paymentOperations", "payment_operations")
      : [];
    const mesasActivas = Array.isArray(payload?.mesasActivas) ? payload.mesasActivas : [];
    const pedidosHistoricos = Array.isArray(payload?.pedidosHistoricos)
      ? payload.pedidosHistoricos
      : [];

    paymentOperations.forEach((operation) => {
      const context = buildOperationalContextRecord(operation);

      if (context.paymentOperationId) {
        saveContext(
          paymentOperationContexts,
          `${branchId}:${context.paymentOperationId}`,
          context
        );
      }

      if (context.orderId) {
        saveContext(orderContexts, `${branchId}:${context.orderId}`, context);
      }
    });

    [...mesasActivas, ...pedidosHistoricos].forEach((order) => {
      const context = buildOperationalContextRecord(order);

      if (context.paymentOperationId) {
        saveContext(
          paymentOperationContexts,
          `${branchId}:${context.paymentOperationId}`,
          context
        );
      }

      if (context.orderId) {
        saveContext(orderContexts, `${branchId}:${context.orderId}`, context);
      }
    });
  });

  return {
    paymentOperationContexts,
    orderContexts,
  };
};

export const isPlatformBackofficeAdminUser = (
  user,
  { isCreatorMode = false } = {}
) => {
  if (isCreatorMode) {
    return true;
  }

  const normalizedEmail = toSafeText(user?.email, 120).toLowerCase();
  return normalizedEmail ? PLATFORM_BACKOFFICE_ADMIN_EMAILS.has(normalizedEmail) : false;
};

export const getCompanyChannelCommissionSettings = async (companyId) => {
  ensureSupabaseReady();

  if (!companyId) {
    return buildDefaultCompanyChannelCommissionSettings();
  }

  const cacheKey = `company-settings:${companyId}`;
  const cachedReport = getCachedBackofficeRead(cacheKey);

  if (cachedReport) {
    return cachedReport;
  }

  const [branches, settingsResult] = await Promise.all([
    loadCompanyBranches(companyId),
    supabase.from("company_settings").select("*").eq("company_id", companyId).maybeSingle(),
  ]);

  if (settingsResult.error) {
    throw normalizeError(
      settingsResult.error,
      "No se pudo cargar la configuracion de comisiones del Backoffice."
    );
  }

  const branchSettingsRows = await loadBranchSettingsRows(
    branches.map((branch) => branch.id)
  );

  return saveCachedBackofficeRead(
    cacheKey,
    normalizeCompanyChannelCommissionSettings({
      settings: settingsResult.data,
      branches,
      branchSettingsRows,
    })
  );
};

export const listPlatformBackofficeCompanies = async () => {
  ensureSupabaseReady();

  const cacheKey = "platform:companies";
  const cachedCompanies = getCachedBackofficeRead(cacheKey);

  if (cachedCompanies) {
    return cachedCompanies;
  }

  const [companies, settingsRows, walletRows, branchRows, branchSettingsRows] =
    await Promise.all([
      loadPlatformCompanies(),
      loadPlatformCompanySettingsRows(),
      loadPlatformWalletRows(),
      loadPlatformBranchRows(),
      loadPlatformBranchSettingsRows(),
    ]);

  const settingsByCompanyId = new Map(
    settingsRows.map((row) => [row.company_id, row])
  );
  const walletByCompanyId = new Map(
    walletRows.map((row) => [row.company_id, row])
  );
  const branchSettingsByBranchId = new Map(
    branchSettingsRows.map((row) => [row.branch_id, row])
  );
  const branchesByCompanyId = branchRows.reduce((accumulator, branch) => {
    const nextCompanyId = branch?.company_id || "";

    if (!nextCompanyId) {
      return accumulator;
    }

    if (!accumulator.has(nextCompanyId)) {
      accumulator.set(nextCompanyId, []);
    }

    accumulator.get(nextCompanyId).push(branch);
    return accumulator;
  }, new Map());

  return saveCachedBackofficeRead(
    cacheKey,
    companies
      .map((company) => {
      const summary = buildDefaultPlatformBackofficeCompanySummary(company);
      const companySettings = settingsByCompanyId.get(company.id) || {};
      const companyWallet = walletByCompanyId.get(company.id) || {};
      const companyBranches = branchesByCompanyId.get(company.id) || [];
      const normalizedWalletProviders = normalizeMenuQrWalletProviders(
        companySettings?.menu_qr_wallet_providers
      );

      const counters = companyBranches.reduce(
        (accumulator, branch) => {
          const branchStatus = normalizeCompanyStatus(branch?.status);
          const serviceMode = normalizeBranchServiceMode(
            branchSettingsByBranchId.get(branch.id)?.service_mode
          );

          accumulator.totalBranches += 1;

          if (branchStatus === "active") {
            accumulator.activeBranches += 1;
          }

          if (serviceMode === "kiosk") {
            accumulator.kioskBranches += 1;
          } else {
            accumulator.salonBranches += 1;
          }

          return accumulator;
        },
        {
          totalBranches: 0,
          activeBranches: 0,
          kioskBranches: 0,
          salonBranches: 0,
        }
      );

      return {
        ...summary,
        billingEmail: toSafeText(companySettings?.billing_email, 160),
        ownerEmail: toSafeText(companySettings?.owner_email, 160),
        bankAlias: toSafeText(companySettings?.bank_alias, 160),
        totalBranches: counters.totalBranches,
        activeBranches: counters.activeBranches,
        kioskBranches: counters.kioskBranches,
        salonBranches: counters.salonBranches,
        walletBalanceUnits: Math.max(
          0,
          Math.round(Number(companyWallet?.balance_units || 0) || 0)
        ),
        walletLowBalanceThreshold: Number(companyWallet?.low_balance_threshold || 0) || 0,
        walletLastTransactionAt: toSafeText(
          companyWallet?.last_transaction_at,
          80
        ),
        allowNegativeBalance: companyWallet?.allow_negative_balance === true,
        negativeBalanceLimit: Number(companyWallet?.negative_balance_limit || 0) || 0,
        manualOrderCommissionRate: normalizeCommissionRate(
          companySettings?.manual_order_commission_rate
        ),
        menuQrCommissionRate: normalizeCommissionRate(
          companySettings?.menu_qr_commission_rate
        ),
        kioskCommissionRate: normalizeCommissionRate(
          companySettings?.kiosk_commission_rate
        ),
        iaCommissionRate: normalizeCommissionRate(
          companySettings?.ia_commission_rate
        ),
        paymentSplitMode: normalizePaymentSplitMode(
          companySettings?.payment_split_mode
        ),
        menuQrWalletProviders: normalizedWalletProviders,
        operationalWalletProvidersCount: getOperationalMenuQrWalletProviders(
          normalizedWalletProviders
        ).length,
      };
      })
      .sort((left, right) => {
        if (left.status !== right.status) {
          return left.status === "active" ? -1 : 1;
        }

        return left.name.localeCompare(right.name, "es", { sensitivity: "base" });
      })
  );
};

export const getCompanyCommissionDebtReport = async (companyId) => {
  ensureSupabaseReady();

  if (!companyId) {
    return buildDefaultCompanyCommissionDebtReport();
  }

  const cacheKey = `company-report:${companyId}`;
  const cachedReport = getCachedBackofficeRead(cacheKey);

  if (cachedReport) {
    return cachedReport;
  }

  const branches = await loadCompanyBranches(companyId);
  const branchIds = branches.map((branch) => branch.id);
  const [ledgerRows, collectionRows, digitalPaymentIntentRows, operationalStateRows] =
    await Promise.all([
      loadBranchCommissionLedgerRows(branchIds),
      loadPlatformCommissionCollectionRows(branchIds),
      loadBranchDigitalPaymentIntentRows(branchIds),
      loadBranchOperationalStateRows(branchIds),
    ]);
  const { paymentOperationContexts, orderContexts } =
    buildOperationalContextIndexes(operationalStateRows);

  const branchesById = new Map(
    branches.map((branch) => [
      branch.id,
      {
        branchId: branch.id,
        branchName: branch.name || "Sucursal",
        status: normalizeCompanyStatus(branch.status),
        outstandingDebtAmount: 0,
        totalCreatedAmount: 0,
        totalAppliedAmount: 0,
        entriesCount: 0,
        lastEntryAt: "",
      },
    ])
  );

  const normalizedEntries = (Array.isArray(ledgerRows) ? ledgerRows : []).map((entry) => {
    const branchId = entry.branch_id || "";
    const orderId = entry.order_id || "";
    const paymentOperationId = entry.payment_operation_id || "";
    const operationalContext =
      paymentOperationContexts.get(`${branchId}:${paymentOperationId}`) ||
      orderContexts.get(`${branchId}:${orderId}`) ||
      {};
    const metadata = mergeLedgerMetadataWithOperationalContext(
      entry.metadata,
      operationalContext
    );

    return {
      id: entry.id || "",
      branchId,
      branchName: branchesById.get(branchId)?.branchName || "Sucursal",
      orderId,
      paymentOperationId,
      entryType: toSafeText(entry.entry_type, 40),
      amount: toMoney(entry.amount),
      sourceMethod: toSafeText(entry.source_method, 60),
      note: toSafeText(entry.note, 240),
      createdAt: entry.created_at || "",
      metadata,
      channelLabel: formatCommissionLedgerChannelLabel({
        ...entry,
        metadata,
      }),
    };
  });

  const normalizedCollectionEntries = (
    Array.isArray(collectionRows) ? collectionRows : []
  ).map((entry) => ({
    id: entry.id || "",
    branchId: entry.branch_id || "",
    branchName:
      branchesById.get(entry.branch_id || "")?.branchName || "Sucursal",
    orderId: entry.order_id || "",
    paymentOperationId: entry.payment_operation_id || "",
    paymentIntentId: entry.payment_intent_id || "",
    collectionType: toSafeText(entry.collection_type, 60),
    amount: toMoney(entry.amount),
    currency: toSafeText(entry.currency, 10) || "ARS",
    status: toSafeText(entry.status, 40),
    sourceChannel: toSafeText(entry.source_channel, 80),
    sourceMethod: toSafeText(entry.source_method, 80),
    channelLabel: formatCommissionCollectionChannelLabel(entry),
    collectedAt: entry.collected_at || "",
    metadata: normalizeJsonRecord(entry.metadata),
  }));

  const normalizedDigitalTransactions = buildBackofficeDigitalTransactions({
    intentRows: digitalPaymentIntentRows,
    collectionRows: normalizedCollectionEntries,
    branchesById,
  });

  normalizedEntries.forEach((entry) => {
    if (!branchesById.has(entry.branchId)) {
      branchesById.set(entry.branchId, {
        branchId: entry.branchId,
        branchName: entry.branchName,
        status: "inactive",
        outstandingDebtAmount: 0,
        totalCreatedAmount: 0,
        totalAppliedAmount: 0,
        entriesCount: 0,
        lastEntryAt: "",
      });
    }

    const currentBranch = branchesById.get(entry.branchId);
    const nextOutstandingDebtAmount = toMoney(
      currentBranch.outstandingDebtAmount +
        entry.amount *
          (COMMISSION_LEDGER_FACTORS[entry.entryType] || 0)
    );

    currentBranch.outstandingDebtAmount = nextOutstandingDebtAmount;
    currentBranch.entriesCount += 1;
    currentBranch.lastEntryAt = currentBranch.lastEntryAt || entry.createdAt || "";

    if (entry.entryType === "debt_created" || entry.entryType === "debt_adjusted") {
      currentBranch.totalCreatedAmount = toMoney(
        currentBranch.totalCreatedAmount + entry.amount
      );
    }

    if (entry.entryType === "debt_applied" || entry.entryType === "debt_cleared") {
      currentBranch.totalAppliedAmount = toMoney(
        currentBranch.totalAppliedAmount + entry.amount
      );
    }
  });

  const branchRows = Array.from(branchesById.values()).sort((left, right) => {
    if (right.outstandingDebtAmount !== left.outstandingDebtAmount) {
      return right.outstandingDebtAmount - left.outstandingDebtAmount;
    }

    return left.branchName.localeCompare(right.branchName, "es", {
      sensitivity: "base",
    });
  });

  const totalCreatedAmount = toMoney(
    branchRows.reduce((sum, branch) => sum + toMoney(branch.totalCreatedAmount), 0)
  );
  const totalAppliedAmount = toMoney(
    branchRows.reduce((sum, branch) => sum + toMoney(branch.totalAppliedAmount), 0)
  );
  const totalCurrentCommissionCollectedAmount = toMoney(
    normalizedCollectionEntries.reduce(
      (sum, entry) =>
        entry.status === "confirmed" && entry.collectionType === "current_commission"
          ? sum + entry.amount
          : sum,
      0
    )
  );
  const totalDebtRecoveredAmount = toMoney(
    normalizedCollectionEntries.reduce(
      (sum, entry) =>
        entry.status === "confirmed" && entry.collectionType === "debt_recovery"
          ? sum + entry.amount
          : sum,
      0
    )
  );

  return saveCachedBackofficeRead(cacheKey, {
    outstandingDebtAmount: calculateCommissionLedgerOutstanding(normalizedEntries),
    totalCreatedAmount,
    totalAppliedAmount,
    totalCollectedAmount: toMoney(
      totalCurrentCommissionCollectedAmount + totalDebtRecoveredAmount
    ),
    totalCurrentCommissionCollectedAmount,
    totalDebtRecoveredAmount,
    entriesCount: normalizedEntries.length,
    branches: branchRows,
    recentEntries: normalizedEntries.slice(0, 24),
    invoiceEntries: normalizedEntries,
    collectionEntries: normalizedCollectionEntries,
    digitalTransactions: normalizedDigitalTransactions,
  });
};

export const getCompanyAiUsageReport = async (companyId) => {
  ensureSupabaseReady();

  if (!companyId) {
    return buildDefaultCompanyAiUsageReport();
  }

  const cacheKey = `company-ai-report:${companyId}`;
  const cachedReport = getCachedBackofficeRead(cacheKey);

  if (cachedReport) {
    return cachedReport;
  }

  const [branches, aiUsageResponse] = await Promise.all([
    loadCompanyBranches(companyId),
    loadCompanyAiUsageRows(companyId),
  ]);
  const branchesById = new Map(
    (Array.isArray(branches) ? branches : []).map((branch) => [
      branch.id,
      {
        branchId: branch.id,
        branchName: branch.name || "Sucursal",
        entriesCount: 0,
        totalTokens: 0,
        totalCostUsd: 0,
        totalCostArs: 0,
        pricedEntriesCount: 0,
        lastEntryAt: "",
      },
    ])
  );

  const normalizedEntries = (Array.isArray(aiUsageResponse.rows)
    ? aiUsageResponse.rows
    : []
  ).map((entry) => {
    const branchId = toSafeText(entry?.branch_id, 120);
    const metadata = normalizeJsonRecord(entry?.metadata);
    const providerDebug = normalizeJsonRecord(metadata.providerDebug);
    const action = normalizeJsonRecord(metadata.action);
    const internalPlan = normalizeJsonRecord(metadata.internalPlan);
    const submittedOrderSnapshot = normalizeJsonRecord(metadata.submittedOrder);

    return {
      id: toSafeText(entry?.id, 120),
      branchId,
      branchName:
        branchesById.get(branchId)?.branchName ||
        toSafeText(metadata.branchName, 120) ||
        "Sucursal",
      orderId: toSafeText(entry?.order_id, 160),
      qrToken: toSafeText(entry?.qr_token, 160),
      customerSessionId: toSafeText(entry?.customer_session_id, 160),
      provider: toSafeText(entry?.provider, 60),
      model: toSafeText(entry?.model, 120),
      status: toSafeText(entry?.status, 40) || "completed",
      promptTokens: Math.max(0, Math.round(Number(entry?.prompt_tokens || 0) || 0)),
      completionTokens: Math.max(
        0,
        Math.round(Number(entry?.completion_tokens || 0) || 0)
      ),
      totalTokens: Math.max(0, Math.round(Number(entry?.total_tokens || 0) || 0)),
      cachedInputTokens: Math.max(
        0,
        Math.round(Number(entry?.cached_input_tokens || 0) || 0)
      ),
      reasoningTokens: Math.max(
        0,
        Math.round(Number(entry?.reasoning_tokens || 0) || 0)
      ),
      estimatedInputCostUsd:
        entry?.estimated_input_cost_usd == null
          ? null
          : toPreciseNumber(entry.estimated_input_cost_usd, 8),
      estimatedOutputCostUsd:
        entry?.estimated_output_cost_usd == null
          ? null
          : toPreciseNumber(entry.estimated_output_cost_usd, 8),
      estimatedTotalCostUsd:
        entry?.estimated_total_cost_usd == null
          ? null
          : toPreciseNumber(entry.estimated_total_cost_usd, 8),
      usdToArsRate:
        entry?.usd_to_ars_rate == null
          ? null
          : toPreciseNumber(entry.usd_to_ars_rate, 4),
      estimatedTotalCostArs:
        entry?.estimated_total_cost_ars == null
          ? null
          : toMoney(entry.estimated_total_cost_ars),
      questionPreview: toSafeText(entry?.question_preview, 800),
      responsePreview: toSafeText(entry?.response_preview, 1200),
      questionFull: toSafeText(
        metadata.fullQuestion || metadata.questionFull || entry?.question_preview,
        2400
      ),
      responseFull: toSafeText(
        metadata.fullAnswer || metadata.answerFull || entry?.response_preview,
        7000
      ),
      contextualQuestion: toSafeText(
        metadata.contextualQuestion || metadata.questionContext,
        2800
      ),
      rawAnswerText: toSafeText(
        metadata.rawAnswerText || metadata.rawProviderText,
        9000
      ),
      debugVersion: toSafeText(metadata.debugVersion, 24),
      decisionParsed: metadata.decisionParsed === true,
      failureStage: toSafeText(metadata.failureStage, 120),
      failureMessage: toSafeText(metadata.failureMessage, 500),
      fallbackProvider: toSafeText(
        metadata.fallbackProvider || providerDebug.primaryProvider,
        60
      ),
      providerFallbackReason: toSafeText(
        metadata.providerFallbackReason || providerDebug.providerFallbackReason,
        320
      ),
      requestedProvider: toSafeText(
        metadata.requestedProvider || providerDebug.requestedProvider,
        60
      ),
      usedProvider: toSafeText(providerDebug.usedProvider || entry?.provider, 60),
      usedModel: toSafeText(providerDebug.usedModel || entry?.model, 120),
      suggestions: normalizeDebugStringList(metadata.suggestions, 8, 120),
      visibleProductIds: normalizeDebugStringList(metadata.visibleProductIds, 24, 120),
      action: {
        type: toSafeText(action.type, 40),
        productId: toSafeText(action.productId, 120),
        targetProductId: toSafeText(action.targetProductId, 120),
        cartItemId: toSafeText(action.cartItemId, 160),
        quantity: Math.max(1, Math.round(Number(action.quantity || 1) || 1)),
        confidence:
          action.confidence == null ? null : toPreciseNumber(action.confidence, 4),
        reason: toSafeText(action.reason, 220),
      },
      internalPlan: {
        focusLevel: toSafeText(internalPlan.focusLevel, 60),
        focusLabel: toSafeText(internalPlan.focusLabel, 160),
        mainProductName: toSafeText(internalPlan.mainProductName, 160),
        customerGoal: toSafeText(internalPlan.customerGoal, 220),
        requestedIngredientChanges: normalizeDebugStringList(
          internalPlan.requestedIngredientChanges,
          8,
          100
        ),
        relationOptions: normalizeDebugStringList(
          internalPlan.relationOptions,
          12,
          140
        ),
        nextStep: toSafeText(internalPlan.nextStep, 220),
      },
      conversationSnapshot: normalizeAiConversationSnapshot(metadata.conversation),
      cartSnapshot: normalizeAiCartSnapshot(metadata.cartItems),
      submittedOrderSnapshot: {
        orderId: toSafeText(submittedOrderSnapshot.orderId, 160),
        preparationNumber: normalizePreparationNumber(
          submittedOrderSnapshot.preparationNumber
        ),
        paymentStatus: toSafeText(submittedOrderSnapshot.paymentStatus, 80),
        checkoutMethod: toSafeText(submittedOrderSnapshot.checkoutMethod, 80),
        totalAmount: toMoney(submittedOrderSnapshot.totalAmount),
        awaitingPaymentApproval:
          submittedOrderSnapshot.awaitingPaymentApproval === true,
        items: (Array.isArray(submittedOrderSnapshot.items)
          ? submittedOrderSnapshot.items
          : []
        )
          .map((item) => {
            const payload = normalizeJsonRecord(item);
            return {
              productName: toSafeText(payload.productName || payload.name, 120),
              quantity: Math.max(
                1,
                Math.round(Number(payload.quantity || 1) || 1)
              ),
            };
          })
          .filter((item) => item.productName)
          .slice(0, 8),
      },
      customerName: toSafeText(metadata.customerName, 80),
      tableLabel: toSafeText(metadata.tableLabel, 120),
      createdAt: toSafeText(entry?.created_at, 80),
      metadata,
    };
  });

  normalizedEntries.forEach((entry) => {
    if (!branchesById.has(entry.branchId)) {
      branchesById.set(entry.branchId, {
        branchId: entry.branchId,
        branchName: entry.branchName,
        entriesCount: 0,
        totalTokens: 0,
        totalCostUsd: 0,
        totalCostArs: 0,
        pricedEntriesCount: 0,
        lastEntryAt: "",
      });
    }

    const branchRow = branchesById.get(entry.branchId);
    branchRow.entriesCount += 1;
    branchRow.totalTokens += entry.totalTokens;
    branchRow.totalCostUsd = toPreciseNumber(
      branchRow.totalCostUsd + toPreciseNumber(entry.estimatedTotalCostUsd, 8),
      8
    );
    branchRow.totalCostArs = toMoney(
      branchRow.totalCostArs + toMoney(entry.estimatedTotalCostArs)
    );

    if (entry.estimatedTotalCostUsd != null || entry.estimatedTotalCostArs != null) {
      branchRow.pricedEntriesCount += 1;
    }

    if (!branchRow.lastEntryAt || entry.createdAt > branchRow.lastEntryAt) {
      branchRow.lastEntryAt = entry.createdAt;
    }
  });

  return saveCachedBackofficeRead(cacheKey, {
    trackingReady: aiUsageResponse.trackingReady,
    trackingMessage: aiUsageResponse.trackingMessage,
    entries: normalizedEntries,
    branches: Array.from(branchesById.values())
      .filter((branch) => branch.entriesCount > 0)
      .sort((left, right) => {
        if (right.entriesCount !== left.entriesCount) {
          return right.entriesCount - left.entriesCount;
        }

        return left.branchName.localeCompare(right.branchName, "es", {
          sensitivity: "base",
        });
      }),
  });
};

export const updateCompanyChannelCommissionSettings = async (
  companyId,
  payload = {}
) => {
  ensureSupabaseReady();

  if (!companyId) {
    throw new Error("No encontramos una empresa activa para guardar comisiones.");
  }

  const { data: currentSettingsRow, error: currentSettingsError } = await supabase
    .from("company_settings")
    .select("*")
    .eq("company_id", companyId)
    .maybeSingle();

  if (currentSettingsError) {
    throw normalizeError(
      currentSettingsError,
      "No se pudo leer la configuracion actual del Backoffice."
    );
  }

  const menuQrCommissionRate = normalizeCommissionRate(
    payload?.menuQrCommissionRate ??
      currentSettingsRow?.menu_qr_commission_rate ??
      DEFAULT_MENU_QR_COMMISSION_RATE
  );
  const kioskCommissionRate = normalizeCommissionRate(
    payload?.kioskCommissionRate ??
      currentSettingsRow?.kiosk_commission_rate ??
      DEFAULT_KIOSK_COMMISSION_RATE
  );
  const manualOrderCommissionRate = normalizeCommissionRate(
    payload?.manualOrderCommissionRate ??
      currentSettingsRow?.manual_order_commission_rate ??
      currentSettingsRow?.commission_rate
  );
  const iaCommissionRate = normalizeCommissionRate(
    payload?.iaCommissionRate ??
      currentSettingsRow?.ia_commission_rate ??
      DEFAULT_IA_COMMISSION_RATE
  );
  const paymentSplitMode = normalizePaymentSplitMode(
    payload?.paymentSplitMode ?? currentSettingsRow?.payment_split_mode
  );
  const menuQrWalletProviders = normalizeMenuQrWalletProviders(
    payload?.menuQrWalletProviders ?? currentSettingsRow?.menu_qr_wallet_providers
  );
  const branches = await loadCompanyBranches(companyId);

  const { error: companySettingsError } = await supabase
    .from("company_settings")
    .upsert({
      company_id: companyId,
      manual_order_commission_rate: manualOrderCommissionRate,
      menu_qr_commission_rate: menuQrCommissionRate,
      kiosk_commission_rate: kioskCommissionRate,
      ia_commission_rate: iaCommissionRate,
      payment_split_mode: paymentSplitMode,
      menu_qr_wallet_providers: menuQrWalletProviders,
    });

  if (companySettingsError) {
    if (isMissingBackofficeMenuQrWalletColumnsError(companySettingsError)) {
      throw new Error(
        "Falta aplicar backoffice_menu_qr_wallet_providers_patch.sql en Supabase para guardar las billeteras del Menu QR."
      );
    }

    if (isMissingBackofficeCommissionColumnsError(companySettingsError)) {
      throw new Error(
        "Falta aplicar backoffice_channel_commission_rates_patch.sql en Supabase para guardar las comisiones del Backoffice."
      );
    }

    throw normalizeError(
      companySettingsError,
      "No se pudo guardar la configuracion general de comisiones."
    );
  }

  if (branches.length > 0) {
    const branchSettingsPayload = branches.map((branch) => ({
      branch_id: branch.id,
      manual_order_commission_rate: manualOrderCommissionRate,
      menu_qr_commission_rate: menuQrCommissionRate,
      kiosk_commission_rate: kioskCommissionRate,
      ia_commission_rate: iaCommissionRate,
      commission_rate: manualOrderCommissionRate,
      payment_split_mode: paymentSplitMode,
    }));

    const { error: branchSettingsError } = await supabase
      .from("branch_settings")
      .upsert(branchSettingsPayload);

    if (branchSettingsError) {
      if (isMissingBackofficeCommissionColumnsError(branchSettingsError)) {
        throw new Error(
          "Falta aplicar backoffice_channel_commission_rates_patch.sql en Supabase para bajar las comisiones por canal a las sucursales."
        );
      }

      throw normalizeError(
        branchSettingsError,
        "No se pudieron propagar las comisiones a las sucursales activas."
      );
    }
  }

  invalidateBackofficeReadCache(companyId);
  return getCompanyChannelCommissionSettings(companyId);
};
